import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, createMemoryRouter, Navigate, RouterProvider, useRouteError, Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from './api';
import { AuthProvider, useAuth, isAdmin } from './auth';
import { Layout } from './layout';
import { ToastProvider, Loading, TipScope } from './ui/kit';
import { LoginPage, SetupPage, ForcedPasswordPage } from './pages/Auth';
import { ProjectsPage, ProjectOverview } from './pages/Projects';
import { TeamPage } from './pages/Team';
import { DisciplinesPage } from './pages/Disciplines';
import { WbsPage } from './pages/Wbs';
import { SchedulePage } from './pages/Schedule';
import { OrganizationsPage, UsersPage } from './pages/Admin';
import { AuditPage } from './pages/Audit';
import { DocumentsPage } from './pages/Documents';
import { DocTrailPage } from './pages/DocTrail';
import { ReportsPage } from './pages/Reports';
import { MeasurementsPage } from './pages/Measurements';
import { CostsPage } from './pages/Costs';
import { RisksPage } from './pages/Risks';
import { ChangesPage } from './pages/Changes';
import { MyNotificationsPage, ProjectNotificationsPage } from './pages/Notifications';
import { CashPage } from './pages/Cash';
import './styles.css';

export const DEMO = import.meta.env.MODE === 'demo';

const qc = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 15_000, refetchOnWindowFocus: true, retry: (n, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && n < 2 }
  }
});

function RouteError() {
  const e = useRouteError();
  return <div className="content"><div className="callout crit"><strong>Algo deu errado nesta tela.</strong><div className="small">{e instanceof Error ? e.message : 'Página não encontrada.'}</div><Link to="/">Voltar para Projetos</Link></div></div>;
}
function AdminOnly({ children }: { children: JSX.Element }) {
  const { state } = useAuth();
  return state.status === 'in' && isAdmin(state.me) ? children : <Navigate to="/" replace />;
}

const router = (DEMO ? createMemoryRouter : createBrowserRouter)([
  {
    path: '/', element: <Layout />, errorElement: <RouteError />,
    children: [
      { index: true, element: <TipScope.Provider value="projetos"><ProjectsPage /></TipScope.Provider> },
      { path: 'p/:projectId', element: <TipScope.Provider value="projetos"><ProjectOverview /></TipScope.Provider> },
      { path: 'p/:projectId/equipe', element: <TipScope.Provider value="equipe"><TeamPage /></TipScope.Provider> },
      { path: 'p/:projectId/disciplinas', element: <TipScope.Provider value="disciplinas"><DisciplinesPage /></TipScope.Provider> },
      { path: 'p/:projectId/eap', element: <TipScope.Provider value="eap"><WbsPage /></TipScope.Provider> },
      { path: 'p/:projectId/cronograma', element: <TipScope.Provider value="cronograma"><SchedulePage /></TipScope.Provider> },
      { path: 'p/:projectId/auditoria', element: <TipScope.Provider value="auditoria"><AuditPage /></TipScope.Provider> },
      { path: 'p/:projectId/documentos', element: <TipScope.Provider value="documentos"><DocumentsPage /></TipScope.Provider> },
      { path: 'p/:projectId/rastro', element: <TipScope.Provider value="rastro"><DocTrailPage /></TipScope.Provider> },
      { path: 'p/:projectId/relatorios', element: <TipScope.Provider value="relatorios"><ReportsPage /></TipScope.Provider> },
      { path: 'p/:projectId/medicoes', element: <TipScope.Provider value="medicoes"><MeasurementsPage /></TipScope.Provider> },
      { path: 'p/:projectId/custos', element: <TipScope.Provider value="custos"><CostsPage /></TipScope.Provider> },
      { path: 'p/:projectId/riscos', element: <TipScope.Provider value="riscos"><RisksPage /></TipScope.Provider> },
      { path: 'p/:projectId/mudancas', element: <TipScope.Provider value="mudancas"><ChangesPage /></TipScope.Provider> },
      { path: 'p/:projectId/avisos', element: <TipScope.Provider value="avisos"><ProjectNotificationsPage /></TipScope.Provider> },
      { path: 'avisos', element: <TipScope.Provider value="avisos"><MyNotificationsPage /></TipScope.Provider> },
      { path: 'caixa', element: <TipScope.Provider value="caixa"><CashPage /></TipScope.Provider> },
      { path: 'admin/empresas', element: <TipScope.Provider value="empresas"><AdminOnly><OrganizationsPage /></AdminOnly></TipScope.Provider> },
      { path: 'admin/usuarios', element: <TipScope.Provider value="usuarios"><AdminOnly><UsersPage /></AdminOnly></TipScope.Provider> },
      { path: '*', element: <RouteError /> }
    ]
  }
]);

function Gate() {
  const { state } = useAuth();
  if (state.status === 'loading') return <Loading text="Abrindo o SIDERPROJ..." />;
  if (state.status === 'setup') return <SetupPage />;
  if (state.status === 'anon') return <LoginPage expired={state.expired} />;
  if (state.me.mustChangePassword) return <ForcedPasswordPage />;
  return <RouterProvider router={router} />;
}

async function boot() {
  if (DEMO) (await import('./demo/server')).install();
  createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <ToastProvider>
        <AuthProvider><Gate /></AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>
);
}
void boot();
